import { alpha, type SxProps, type Theme } from '@mui/material/styles';
import { brand, gray } from '@theme/primitives';

/**
 * A filter in the toolbar. Unset, it is a dashed outline that reads as an offer; set, it is
 * solid and carries what it holds, so the toolbar itself says how the list is narrowed and
 * no second row of chips is needed to repeat it.
 */
export const pillStyles = (active: boolean): SxProps<Theme> => ({
  height: 36,
  px: 1.25,
  gap: 0.75,
  flexShrink: 0,
  fontSize: 13.5,
  fontWeight: 500,
  whiteSpace: 'nowrap',
  color: active ? 'text.primary' : 'text.secondary',
  borderStyle: active ? 'solid' : 'dashed',
  borderColor: active ? alpha(brand[400], 0.55) : gray[600],
  backgroundColor: active ? alpha(brand[900], 0.35) : gray[800],
  '& svg': { flexShrink: 0, color: active ? brand[300] : gray[400] },
  '&:hover': {
    borderColor: active ? brand[400] : gray[500],
    backgroundColor: active ? alpha(brand[900], 0.55) : gray[900],
  },
});

/** The line between a pill's name and what it holds. */
export const pillDividerStyles: SxProps<Theme> = {
  alignSelf: 'stretch',
  my: 0.75,
  borderLeft: `1px solid ${alpha(gray[500], 0.5)}`,
};

/** One value a pill holds, or the count that stands for several. */
export const pillValueStyles: SxProps<Theme> = {
  px: 0.75,
  height: 20,
  lineHeight: '20px',
  borderRadius: '4px',
  fontSize: 12,
  fontWeight: 500,
  maxWidth: 140,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  color: brand[100],
  backgroundColor: alpha(brand[400], 0.18),
};

/**
 * The surface a filter opens onto, framed as the theme frames a menu so it reads as one of
 * the app's menus. Its colour is the theme's paper, which carries `!important` and so is
 * what every menu shows whatever it asks for; anything drawn on it matches that, not a grey.
 */
export const popoverPaperStyles: SxProps<Theme> = {
  mt: 0.75,
  backgroundImage: 'none',
  border: (theme) => `1px solid ${theme.palette.divider}`,
  borderRadius: '10px',
  boxShadow: 'hsla(220, 30%, 5%, 0.7) 0px 4px 16px 0px, hsla(220, 25%, 10%, 0.8) 0px 8px 16px -5px',
  overflow: 'hidden',
};

/** The bar at the foot of a filter's popover: what is chosen on the left, the way out on the right. */
export const popoverFooterStyles: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1,
  minHeight: 44,
  px: 1.5,
  py: 0.75,
  borderTop: (theme) => `1px solid ${theme.palette.divider}`,
  backgroundColor: alpha(gray[700], 0.18),
};

/** The checkbox drawn beside an option: the theme's own checkbox, which the listbox cannot host as an input. */
export const optionCheckStyles = (checked: boolean): SxProps<Theme> => ({
  width: 16,
  height: 16,
  flexShrink: 0,
  borderRadius: '5px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid',
  borderColor: checked ? brand[500] : alpha(gray[600], 0.9),
  backgroundColor: checked ? brand[500] : alpha(gray[900], 0.8),
  color: 'common.white',
  transition: 'background-color 120ms ease-in, border-color 120ms ease-in',
});
